import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Download, PieChart } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const eur = (n) => `${Math.round(Number(n || 0)).toLocaleString("fr-FR")} €`;
const pct = (n) => (n === null || n === undefined ? "-" : `${String(n).replace(".", ",")} %`);

function Tuile({ valeur, libelle, detail }) {
  return (
    <Card><CardContent className="p-4">
      <p className="text-2xl font-bold text-secondary">{valeur}</p>
      <p className="text-sm text-muted-foreground">{libelle}</p>
      {detail && <p className="text-xs text-muted-foreground mt-1">{detail}</p>}
    </CardContent></Card>
  );
}

function Section({ titre, children }) {
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{titre}</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{children}</div>
    </div>
  );
}

export default function TableauAssociationPage() {
  const anneeCourante = new Date().getFullYear();
  const [annee, setAnnee] = useState(anneeCourante);
  const [d, setD] = useState(null);

  useEffect(() => {
    setD(null);
    axios.get(`${API}/tableau-association`, { params: { annee } })
      .then((r) => setD(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Chargement impossible"));
  }, [annee]);

  const pdf = async () => {
    try {
      const r = await axios.get(`${API}/tableau-association/pdf`, { params: { annee }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `rapport_activite_ftu_${annee}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF indisponible"); }
  };

  return (
    <div className="space-y-6" data-testid="tableau-association">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-secondary flex items-center gap-2">
            <PieChart className="w-6 h-6 text-primary" /> Tableau de bord de l'association
          </h1>
          <p className="text-sm text-muted-foreground">
            L'activité du réseau sur l'année. Le rapport PDF ne contient que des totaux : il peut être remis en
            assemblée générale ou aux partenaires.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <select className="h-9 border rounded-md px-2 text-sm" value={annee} onChange={(e) => setAnnee(parseInt(e.target.value, 10))}>
            {[0, 1, 2].map((k) => anneeCourante - k).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <Button onClick={pdf} disabled={!d}><Download className="w-4 h-4 mr-1" /> Rapport d'activité (PDF)</Button>
        </div>
      </div>

      {!d ? <p className="text-muted-foreground">Chargement…</p> : (
        <>
          <Section titre="Le réseau">
            <Tuile valeur={d.adherents.actifs} libelle="food trucks actifs" detail={`${d.adherents.total} inscrits au total`} />
            <Tuile valeur={d.adherents.nouveaux} libelle={`nouveaux adhérents en ${annee}`} />
            <Tuile valeur={d.adherents.en_attente} libelle="adhésions en attente de validation" />
            <Tuile valeur={d.activite.camions_mobilises} libelle="camions mobilisés sur au moins un service" />
          </Section>

          <Section titre="L'activité">
            <Tuile valeur={d.activite.evenements_realises} libelle="événements réalisés" />
            <Tuile valeur={d.activite.services_total} libelle="services assurés"
              detail={`${d.activite.services_evenements} sur événements, ${d.activite.placements_directs} placements directs, ${d.activite.hec_services} à HEC (${d.activite.hec_jours} jours)`} />
            <Tuile valeur={pct(d.activite.taux_remplissage)} libelle="taux de remplissage"
              detail={d.activite.places_offertes ? `${d.activite.places_pourvues} places pourvues sur ${d.activite.places_offertes} demandées` : "Aucun événement avec un nombre de camions demandé"} />
            <Tuile valeur={d.vivier.missions} libelle="missions d'extras du vivier" detail={`${String(d.vivier.heures).replace(".", ",")} h · ${d.vivier.extras} extras disponibles`} />
          </Section>

          <Section titre="Les retombées pour les adhérents">
            <Tuile valeur={eur(d.chiffre_affaires.total)} libelle="chiffre d'affaires déclaré"
              detail={`dont ${eur(d.chiffre_affaires.bilans_manuels)} de bilans hors application`} />
            <Tuile valeur={pct(d.chiffre_affaires.taux_retour)} libelle="des bilans de prestation remis"
              detail={`${d.chiffre_affaires.bilans_recus} sur ${d.chiffre_affaires.bilans_attendus} attendus`} />
            <Tuile valeur={d.chiffre_affaires.moyenne_par_bilan ? eur(d.chiffre_affaires.moyenne_par_bilan) : "-"} libelle="de CA moyen par service déclaré" />
          </Section>

          <Section titre="La vie de l'association">
            <Tuile valeur={pct(d.cotisations.taux)} libelle="des cotisations à jour"
              detail={`${d.cotisations.payees} payées, ${d.cotisations.offertes} offertes, ${d.cotisations.impayees} impayées`} />
            <Tuile valeur={eur(d.cotisations.encaisse)} libelle="de cotisations encaissées" detail={`${eur(d.cotisations.reste_du)} restant dus`} />
            <Tuile valeur={eur(d.frais_services.encaisse)} libelle="de frais de service encaissés" detail={`${eur(d.frais_services.en_attente)} en attente`} />
          </Section>

          <Section titre="Engagement RSE">
            <Tuile valeur={d.rse.signataires} libelle="signataires de la charte" detail={`${pct(d.rse.taux_signature)} des adhérents actifs`} />
            <Tuile valeur={d.rse.bilans_valides} libelle={`bilans RSE ${annee} validés`} />
            <Tuile valeur={pct(d.rse.achats_locaux_pct)} libelle="d'achats locaux en moyenne" />
            <Tuile valeur={d.rse.huile_litres ? `${d.rse.huile_litres} L` : "-"} libelle="d'huile usagée recyclée" />
          </Section>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Services et chiffre d'affaires par mois</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[["services", "Services assurés", (v) => v], ["ca", "CA déclaré (€)", (v) => eur(v)]].map(([k, titre, fmt]) => (
                <div key={k} className="h-56">
                  <p className="text-xs text-muted-foreground mb-1">{titre}</p>
                  <ResponsiveContainer width="100%" height="90%">
                    <BarChart data={d.par_mois}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} width={50} />
                      <Tooltip formatter={(v) => fmt(v)} />
                      <Bar dataKey={k} fill="#1f4e3d" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Événements au plus fort chiffre d'affaires</CardTitle></CardHeader>
              <CardContent className="space-y-1 text-sm">
                {d.detail.top_evenements_ca.length === 0 && <p className="text-muted-foreground">Aucun bilan reçu.</p>}
                {d.detail.top_evenements_ca.map((e) => (
                  <div key={e.evenement} className="flex justify-between gap-2 border-b last:border-0 py-1">
                    <span className="truncate">{e.evenement}</span><strong>{eur(e.ca)}</strong>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Camions les plus mobilisés</CardTitle></CardHeader>
              <CardContent className="space-y-1 text-sm">
                {d.detail.top_camions_services.length === 0 && <p className="text-muted-foreground">Aucun service sur l'année.</p>}
                {d.detail.top_camions_services.map((c) => (
                  <div key={c.nom} className="flex justify-between gap-2 border-b last:border-0 py-1">
                    <span className="truncate">{c.nom}</span><strong>{c.services} services</strong>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
          <p className="text-xs text-muted-foreground">
            Chiffre d'affaires : montants déclarés par les camions dans leurs bilans de prestation (et bilans saisis hors application).
            Les services HEC et les placements directs sans bilan n'y figurent pas. Ces deux listes détaillées restent réservées au bureau :
            elles ne figurent pas dans le rapport PDF.
          </p>
        </>
      )}
    </div>
  );
}
